/**
 * Kart PIN deposu (DEV-3904).
 *
 * PIN'ler eskiden localStorage `pinCodes` anahtarında DÜZ METİN duruyordu. Kabuk
 * 2.0.5 `window.zirvelib.guvenliDepo` (Electron safeStorage: Windows DPAPI /
 * macOS Keychain) sunuyorsa PIN'ler şifreli `pinCodesSifreli` anahtarında tutulur.
 * guvenliDepo yoksa (eski kabuk, tarayıcı/icrapro-app) ya da kullanilabilir()
 * false dönerse eski düz metin davranışı AYNEN sürer.
 *
 * Şifre çözme asenkron (IPC), `kartPinKayitliMi` ise senkron kalmak zorunda:
 * bu yüzden açılışta `yukle()` ile çözülmüş kopya BELLEĞE alınır, okumalar bellekten
 * gelir. Yazma senkron API'yi (`kaydetSenkron`) korur, kalıcılaştırma arkada sıralı
 * yapılır. Bellekteki kopya bu pencerenin JS'ine açıktır: şifreleme DİSKTEKİ
 * (at-rest) kopyayı korur, sayfa içi saldırganı değil.
 *
 * İki depo birlikte bulunabilir: güvenli depo o oturumda kullanılamayıp (anahtarlık kilitli vb.)
 * düz modda PIN yazılırsa şifreli kopya DURUR (o oturumda görünmeyen kartlar kaybolmasın) ve
 * düz modda yazılan/silinen kartların seri numaraları `pinCodesDuzDegisen`e işlenir. Sonraki
 * güvenli açılışta bu kartlar için düz kopya, diğerleri için şifreli kopya geçerlidir.
 *
 * Saf tutulur: depo ve guvenliDepo dışarıdan verilir, birim testten geçebilsin.
 */

export interface PinKaydi {
    cardOwnerName?: string;
    cardSerialNumber: string;
    pinCode: string;
    autoLogin?: boolean;
}

export interface GuvenliDepo {
    kullanilabilir(): Promise<boolean>;
    sifrele(metin: string): Promise<string>;
    coz(sifreli: string): Promise<string>;
}

export interface BasitDepo {
    getItem(anahtar: string): string | null;
    setItem(anahtar: string, deger: string): void;
    removeItem(anahtar: string): void;
}

export const PIN_DUZ_ANAHTAR = 'pinCodes';
export const PIN_SIFRELI_ANAHTAR = 'pinCodesSifreli';
/**
 * Şifreli kopya varken düz modda yazılan/silinen kartların seri numaraları (JSON dizi).
 * Yalnız şifreli kopya durduğu sürece vardır; şifreli kopya yeniden yazılıp doğrulanınca silinir.
 */
export const PIN_DUZ_DEGISEN_ANAHTAR = 'pinCodesDuzDegisen';

function kayitlariAyikla(ham: any): PinKaydi[] {
    if (!Array.isArray(ham)) {
        return [];
    }
    return ham.filter((k: any) => k && typeof k === 'object' && typeof k.cardSerialNumber === 'string');
}

export class PinDeposu {
    private onbellek: PinKaydi[] | null = null;
    private mod: 'duz' | 'sifreli' = 'duz';
    private yuklemeSozu: Promise<void> | null = null;
    private yazmaZinciri: Promise<void> = Promise.resolve();
    private depo: () => BasitDepo;
    private guvenliDepo: () => GuvenliDepo | null;

    constructor(depo: () => BasitDepo, guvenliDepo: () => GuvenliDepo | null) {
        this.depo = depo;
        this.guvenliDepo = guvenliDepo;
    }

    /** Kalıcı depoyu açar (idempotent). Hata fırlatmaz: bozulursa düz moda düşer. */
    yukle(): Promise<void> {
        if (!this.yuklemeSozu) {
            this.yuklemeSozu = this.yukleGercek().catch((e: any) => {
                console.warn('PIN deposu yüklenemedi, düz metin depoya dönülüyor:', e);
                this.mod = 'duz';
                this.onbellek = this.duzOku();
            });
        }
        return this.yuklemeSozu;
    }

    /** Senkron okuma. Yükleme bitmediyse eski davranış: düz anahtardan okur. */
    oku(): PinKaydi[] {
        const kaynak = this.onbellek !== null ? this.onbellek : this.duzOku();
        return JSON.parse(JSON.stringify(kaynak));
    }

    /** Senkron API: bellek hemen güncellenir, kalıcılaştırma arkada sıralanır. */
    kaydetSenkron(kayitlar: PinKaydi[]): void {
        this.kaydet(kayitlar).catch((e: any) => console.warn('PIN deposu yazılamadı:', e));
    }

    /** Bellek hemen güncellenir; dönen söz kalıcı yazım bitince çözülür. */
    kaydet(kayitlar: PinKaydi[]): Promise<void> {
        this.onbellek = JSON.parse(JSON.stringify(kayitlar));
        const yazim = this.yazmaZinciri.then(() => this.kalicilastir());
        this.yazmaZinciri = yazim.catch(() => undefined);
        return yazim;
    }

    sifreliMi(): boolean {
        return this.mod === 'sifreli';
    }

    private duzOku(): PinKaydi[] {
        try {
            const ham = this.depo().getItem(PIN_DUZ_ANAHTAR);
            return ham ? kayitlariAyikla(JSON.parse(ham)) : [];
        } catch (e) {
            return [];
        }
    }

    private degisenOku(): Set<string> {
        try {
            const ham = this.depo().getItem(PIN_DUZ_DEGISEN_ANAHTAR);
            const liste = ham ? JSON.parse(ham) : [];
            return new Set<string>(Array.isArray(liste) ? liste.filter((x: any) => typeof x === 'string') : []);
        } catch (e) {
            return new Set<string>();
        }
    }

    private async yukleGercek(): Promise<void> {
        const guvenli = this.guvenliDepo();
        let sifreliKullanilabilir = false;
        if (guvenli && typeof guvenli.kullanilabilir === 'function'
            && typeof guvenli.sifrele === 'function' && typeof guvenli.coz === 'function') {
            try {
                sifreliKullanilabilir = (await guvenli.kullanilabilir()) === true;
            } catch (e) {
                sifreliKullanilabilir = false;
            }
        }
        if (!guvenli || !sifreliKullanilabilir) {
            // Düz modda çözülecek bir şey yok: bellek kopyası ALINMAZ, okumalar canlı düz anahtardan
            // gelir (eski davranış). ImzaClient kurulurken yukle()'yi kendisi başlattığı için, burada
            // kopya alınsaydı icrapro-app'te (güvenli depo yok) kurulumdan sonra düz anahtara
            // yazılanlar görünmez, eski kopya okunurdu. İlk yazımdan sonra bellek kopyası kurulur.
            this.mod = 'duz';
            return;
        }

        const duz = this.duzOku();
        let sifreli: PinKaydi[] | null = null;
        const sifreliHam = this.depo().getItem(PIN_SIFRELI_ANAHTAR);
        if (sifreliHam) {
            try {
                sifreli = kayitlariAyikla(JSON.parse(await guvenli.coz(sifreliHam)));
            } catch (e) {
                // Çözülemedi (bozuk kayıt, başka Windows kullanıcısı/makine). Düz kopya
                // varsa ondan devam edilir; yoksa boş başlanır ve kullanıcı PIN'i bir kez girer.
                console.warn('Şifreli PIN kaydı çözülemedi:', e);
                sifreli = null;
            }
        }

        // Birleştirme. Düz modda dokunulan kartlar (`pinCodesDuzDegisen`) için DÜZ kopya geçerlidir:
        // oradaki PIN daha yenidir, düz kopyada artık yoksa kart düz modda silinmiştir (geri gelmez).
        // Dokunulmayan kartlar şifreliden gelir (düz modda hiç görünmediler, PIN'leri kaybolmaz).
        // İşaret yoksa (eski sürümden göç ya da şifreli yazım ile düz kopyanın silinmesi arasında
        // yarım kalmış yazım: şifreli daha yeni) şifreli kayıt kazanır, yalnız düzdeki kartlar eklenir.
        const duzDegisen = this.degisenOku();
        const birlesik: PinKaydi[] = (sifreli || []).filter((k) => !duzDegisen.has(k.cardSerialNumber));
        for (const kayit of duz) {
            if (!birlesik.some((k) => k.cardSerialNumber === kayit.cardSerialNumber)) {
                birlesik.push(kayit);
            }
        }
        this.mod = 'sifreli';
        this.onbellek = birlesik;

        // Düz kopya yalnız şifreli kopya YAZILIP GERİ OKUNARAK doğrulanırsa silinir
        // (kalicilastir doğrulayamazsa düz moda düşer ve PIN'ler kaybolmaz). Düz modda dokunulan
        // kart varsa şifreli kopya da yeniden yazılır: diskteki eski şifreli kopyada o kartların
        // bayat PIN'i duruyor, işaret silinince geri gelirdi.
        if (duz.length > 0 || (sifreli !== null && duzDegisen.size > 0)) {
            await this.kalicilastir();
        } else if (sifreli !== null && this.depo().getItem(PIN_DUZ_ANAHTAR) !== null) {
            // Şifreli kayıt çözüldü, düz anahtardan geriye yalnız boş kabuk kalmış.
            this.depo().removeItem(PIN_DUZ_ANAHTAR);
            this.depo().removeItem(PIN_DUZ_DEGISEN_ANAHTAR);
        }
    }

    /** Şifreli modda yaz → geri oku → doğrula → düz kopyayı sil. Doğrulanamazsa düz moda düş. */
    private async kalicilastir(): Promise<void> {
        const kayitlar = this.onbellek || [];
        const depo = this.depo();
        const guvenli = this.guvenliDepo();
        if (this.mod !== 'sifreli' || !guvenli) {
            // Yazmadan önceki düz kopya: silinen kartlar da "dokunulmuş" sayılsın.
            const onceki = this.duzOku();
            depo.setItem(PIN_DUZ_ANAHTAR, JSON.stringify(kayitlar));
            if (depo.getItem(PIN_SIFRELI_ANAHTAR) === null) {
                // Korunacak şifreli kopya yok (icrapro-app, eski kabuk): izlenecek bir şey yok.
                depo.removeItem(PIN_DUZ_DEGISEN_ANAHTAR);
                return;
            }
            // Şifreli kopya bu oturumda okunamadı: yazımı (değişen/silinen PIN'leri) içermiyor. Silinse
            // görünmeyen kartların PIN'i de giderdi; durursa da bayat PIN'i geri getirir ve sessiz giriş
            // yanlış PIN denemesi harcardı (kart 3 denemede kilitlenir). Çözüm: dokunulan kartlar
            // işaretlenir, sonraki güvenli açılışta yalnız onlar için düz kopya kazanır (yukleGercek).
            const degisen = this.degisenOku();
            onceki.concat(kayitlar).forEach((k) => degisen.add(k.cardSerialNumber));
            depo.setItem(PIN_DUZ_DEGISEN_ANAHTAR, JSON.stringify(Array.from(degisen)));
            return;
        }
        if (kayitlar.length === 0) {
            depo.removeItem(PIN_SIFRELI_ANAHTAR);
            depo.removeItem(PIN_DUZ_ANAHTAR);
            depo.removeItem(PIN_DUZ_DEGISEN_ANAHTAR);
            return;
        }
        const duzMetin = JSON.stringify(kayitlar);
        let dogrulandi = false;
        try {
            depo.setItem(PIN_SIFRELI_ANAHTAR, await guvenli.sifrele(duzMetin));
            dogrulandi = (await guvenli.coz(depo.getItem(PIN_SIFRELI_ANAHTAR) || '')) === duzMetin;
        } catch (e) {
            console.warn('PIN şifreli yazılamadı:', e);
        }
        if (dogrulandi) {
            depo.removeItem(PIN_DUZ_ANAHTAR);
            depo.removeItem(PIN_DUZ_DEGISEN_ANAHTAR);
            return;
        }
        // Doğrulanamadı: PIN KAYBETME. Düz metne dön, şifreli kopyayı bırak (bozuk olabilir).
        console.warn('Şifreli PIN kopyası doğrulanamadı; düz metin depo kullanılıyor.');
        this.mod = 'duz';
        depo.removeItem(PIN_SIFRELI_ANAHTAR);
        depo.removeItem(PIN_DUZ_DEGISEN_ANAHTAR);
        depo.setItem(PIN_DUZ_ANAHTAR, duzMetin);
    }
}
